import { Component } from 'react';
import IbssDialog from '../../Dialogs/BaseDialog/IbssDialog';
import { appContext } from '../../../AppContext';
import EquipmentList from './EquipmentList';
import EquipmentSummary from './EquipmentSummary';
import { Box, Divider } from '@mui/material';
import { getFloorNameUsingFloorAndBuildingId } from '../../../Common/Helper';
import { PagedResponse } from '../../../Providers.Api/Models';
import { AxiosResponse } from 'axios';
import apis from '../../../Providers.Api/apis';
import { Constants } from '../../../Common/Constants';
import { DateTime } from 'luxon';
import { EquipmentView, stores } from './DataModels';

class EquipmentsDialog extends Component<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get apiClient() { return appContext().apiClient; }
    private get buildingId(): number { return parseInt(this.props.buildingId) }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            bookedEquipment: [], // equipment already booked by user
            equipments: [], // all equipment
            selectedEquipmentIds: [], // clicked equipment list items.
            isLoading: false,
            hearingAidsBooked: 0,
            presentationAidsBooked: 0,
        };
    }

    public async componentDidMount(): Promise<void>
    {
        if(this.props.floorId && this.props.bookingId)
        {
            this.getBookedParties();
        }
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        if(prevProps.bookingId !== this.props.bookingId && this.props.bookingId)
        {
            this.getBookedParties();
            await this.getEquipment();
        }
        
        if(prevProps.show !== this.props.show && this.props.bookingId)
        {
            this.getBookedParties();
            await this.getEquipment();
        }

        if(prevState.hearingAidsBooked !== this.state.hearingAidsBooked || prevState.presentationAidsBooked !== this.state.presentationAidsBooked)
        {
            if(this.props.bookedEquipmentChanged)
            {
                this.props.bookedEquipmentChanged(this.state.hearingAidsBooked, this.state.presentationAidsBooked);
            }
        }
    }

    private async getEquipment(): Promise<void>
    {
        if (this.props.equipmentType == "" ||
            this.props.bookingStart == "" ||
            this.props.bookingEnd == "" ||
            this.props.buildingId == "")
        {
            return;
        }
        const buildingId = this.props.buildingId;
        try
        {
            this.setState({isLoading: true});

            // Fetch store list
            const storeList = await appContext().ibssApiClientV2.v2.byNodeid.spaces.bySpaceid.stores.get<PagedResponse<stores[]>>({
                nodeId: this.buildingId,
                spaceid: this.props.spaceId,
                select: stores,
            });

            const storeSpaceIds = storeList.value.map(store => store.Space_Id);
            let filter = 'Equip_IsEnabled eq 1';
            if (storeSpaceIds.length > 0)
            {
                const spaceFilter = storeSpaceIds.map(id => `Store_Space_Id eq '${id}'`).join(' or ');
                filter += ` and (${spaceFilter})`;
            }
                         
            const allEquipments = await appContext().ibssApiClientV2.v2.byNodeid.equipment.get<PagedResponse<EquipmentView[]>>
            ({
                nodeId: this.buildingId,
                select: EquipmentView,
                filter: filter
            });
            const allStoreEquipments = allEquipments.value.filter(equipment => storeSpaceIds.includes(equipment.Store_Space_Id));

            const body=
            {
                EquipmentType: this.props.equipmentType,
                StartDate: DateTime.fromISO(this.props.bookingStart).toUtcByNode(Number(this.props.buildingId)).toISO(),
                EndDate: DateTime.fromISO(this.props.bookingEnd).toUtcByNode(Number(this.props.buildingId)).toISO()
            }
            const availableEquipments = await appContext().ibssApiClientV2.v2.byNodeid.equipment.available.post<EquipmentView[]>({
                nodeId: this.buildingId,
                body : body
            })

            // Filter equipment based on store space IDs
            const filteredEquipments = availableEquipments.filter(equipment => storeSpaceIds.includes(equipment.Store_Space_Id));
            const storeEquipment = filteredEquipments.map(i => Equipment.fromUpdateEquipmentResponse(i, buildingId));

            // Get booked equipment data from allEquipments
            const bookedEquipmentList: Equipment[] = this.state.bookedEquipment
                .map(booked =>
                {
                    const bookedEquipmentData = allStoreEquipments.find(equipment => equipment.Equip_Id === booked.Booking_Resource_Id);
                    if (bookedEquipmentData && bookedEquipmentData.Equip_Type === this.props.equipmentType)
                    {
                        return Equipment.fromEquipmentDetailsResponse(bookedEquipmentData, buildingId.toString());
                    }
                    return null;
                    
                }).filter(Boolean) as Equipment[];

            // Update equipments array with booked equipment data
            bookedEquipmentList.forEach(bookedEquipmentView =>
            {
                const duplicateEquipmentIndex = storeEquipment.findIndex(i => i.equipmentId === bookedEquipmentView.equipmentId);
                if(duplicateEquipmentIndex !== -1)
                {
                    // if duplicate equipment Id, replace it
                    storeEquipment[duplicateEquipmentIndex] = bookedEquipmentView;
                } 
                else
                {
                    storeEquipment.push(bookedEquipmentView);
                }
            });

            const hearingAids = bookedEquipmentList.filter(i => i.equipmentType === "HearingAid");
            const presentationAids = bookedEquipmentList.filter(i => i.equipmentType === "PresentationAid");

            this.setState({
                equipments: storeEquipment,
                bookedEquipment: this.state.bookedEquipment.filter(i => bookedEquipmentList.some(j => j.equipmentId === i.Booking_Resource_Id)),
                selectedEquipmentIds: this.state.selectedEquipmentIds.filter(i =>storeEquipment.some(j => j.equipmentId === i) ),
                hearingAidsBooked: hearingAids.length,
                presentationAidsBooked: presentationAids.length,
            });

        }
        catch
        {
            return;
        }
        finally
        {
            this.setState({isLoading: false});
        }
    }


    private handleSelectEquipment(equipmentId: string): void
    {
        // if equipmentId already selected, remove it from state.selectedEquipmentIds, else, add new equipmentId to state.selectedEquipmentIds.
        if(this.state.selectedEquipmentIds.includes(equipmentId))
        {
            this.setState((prevState)=>({selectedEquipmentIds: prevState.selectedEquipmentIds.filter(id => id !== equipmentId)}));
        }
        else
        {
            this.setState((prevState)=>({selectedEquipmentIds: [...prevState.selectedEquipmentIds, equipmentId]}));
        }
    }

    private getBookedParties(): void
    {
        this.setState({ 
            bookedEquipment: this.props.bookedEquipment, 
            selectedEquipmentIds: this.props.bookedEquipment.map((x: { Booking_Resource_Id: string; }) => x.Booking_Resource_Id),
        });
    }

    public render(): JSX.Element
    {
        return (
            <IbssDialog
                aria-modal="true"
                aria-label="cost code modal"
                fullWidth
                maxWidth={'md'}
                open={this.props.show}
                onClose={this.props.onClose}
                header=
                {
                    <>
                        <label className="modal-heading">{this.labels.HubLabelAddYourEquipment}</label>
                    </>
                }
                dialogContent=
                {
                    <Box>
                    <EquipmentList
                        bookingId={this.props.bookingId}
                        bookingStart={this.props.bookingStart}
                        bookingEnd={this.props.bookingEnd}
                        buildingId={this.props.buildingId}
                        equipmentType={this.props.equipmentType}
                        equipments={this.state.equipments}
                        selectedEquipmentIds={this.state.selectedEquipmentIds}
                        selectEquipment={(equipmentId: string) => this.handleSelectEquipment(equipmentId)}
                        isLoading={this.state.isLoading}
                    />
                    <Divider sx={{mt: 2, mb: 2}}/>
                    <EquipmentSummary
                        bookedEquipment={this.props.bookedEquipment}
                        bookingName={this.props.bookingName}
                        bookingStart={this.props.bookingStart}
                        bookingEnd={this.props.bookingEnd}
                        bookingId={this.props.bookingId}
                        buildingId={this.props.buildingId}
                        disabled={this.props?.disabled}
                        equipments={this.state.equipments}
                        equipmentType={this.props.equipmentType}
                        floorId={this.props.floorId}
                        onClose={this.props.onClose}
                        selectedEquipmentIds={this.state.selectedEquipmentIds}
                        spaceId={this.props.spaceId}
                        spaceName={this.props.spaceName}
                        equipmentSubmitted={this.props.equipmentSubmitted}
                    />
                    </Box>
                }
            />
        )
    }
}

export default EquipmentsDialog;

export interface IProps
{
    show: boolean,
    onClose: () => void,
    bookingId: string,
    bookingStart: string,
    bookingEnd: string,
    bookingName: string,
    buildingId: string,
    disabled?: boolean,
    equipmentType: string,
    floorId: number,
    spaceId: string,
    spaceName: string,
    bookedEquipmentChanged?: (hearingAids: number, presentationAids: number) => void,
    equipmentSubmitted?: () => void,
    bookedEquipment: IBookedEquipment[]
}

export interface IState
{
    bookedEquipment: IBookedEquipment[],
    equipments: Equipment[],
    selectedEquipmentIds: string[],
    isLoading: boolean,
    hearingAidsBooked: number,
    presentationAidsBooked: number,
}

export interface IBookedEquipment
{
    Record_Id: string;
    Booking_Participant_Email: string;
    Booking_Participant_Name: string;
    Booking_Participant_Organisation: string;
    Booking_Resource_Id: string;
    Booking_Visitor: boolean;
    Booking_Participant_Type: number;
}

export class Equipment
{
    public static fromResponse(response: EquipmentView, buildingId: string, backgroundColor: string): Equipment
    {
        let equipment = new Equipment();
        equipment.nodeId = response.Node_Id;
        equipment.equipmentId = response.Equip_Id;
        equipment.equipmentName = response.Equip_Name;
        equipment.equipmentType = response.Equip_Type;
        equipment.equipmentClass = response.Equip_Class;
        equipment.imageUrl = ((response.ImageURI || Constants.imagePaths.get(response.Equip_Class)) ?? "");
        equipment.bookingResourceId = response.Booking_Resource_Id;
        equipment.metaLocZone = response.Meta_Loc_Zone;
        equipment.floorName = getFloorNameUsingFloorAndBuildingId(buildingId, response.Node_Id);
        equipment.backgroundColor = backgroundColor;
        return equipment;
    }
    public static fromUpdateEquipmentResponse(response: EquipmentView, buildingId: string): Equipment
    {
        return this.fromResponse(response, buildingId, "");
    }
    public static fromEquipmentDetailsResponse(response: EquipmentView, buildingId: string): Equipment
    {
        return this.fromResponse(response, buildingId, "lightGrey");
    }
    public nodeId = 0;
    public equipmentId = "";
    public equipmentName = "";
    public equipmentType = "";
    public equipmentClass = "";
    public imageUrl = "";
    public bookingResourceId = "";
    public metaLocZone = "";
    public floorName = "";
    public backgroundColor = "";
}
